import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
  Col,
} from 'components';
import PropTypes from 'prop-types';
import { globalPropTypes } from 'helpers';
import { statusOptions, titleOptions } from '../CisOptions';
import {
  exportCertificateHoldersToExcel,
  downloadCertificateHoldersImportTemplate,
} from '../../../api/services/cis';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import checkPermissions from '../../../helpers/checkPermissions';

const CertificateHolders = ({
  loading,
  type,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  setProfile,
  removeCertificateHolder,
  setCertificatesFilters,
  loadCertificateHoldersAttributes,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const isIpmaCvmb = type === 'ipma-cvmb';

  const manageCisPermission =
    (type === 'ipma' && checkPermissions({ roles: ['ipma_cis'] }, userPermissions || [])) ||
    (type === 'ma' && checkPermissions({ roles: ['ma_manage_cis'] }, userPermissions || []));

  useEffect(() => {
    loadCertificateHoldersAttributes();
  }, []);

  useEffect(() => {
    if (isIpmaCvmb) {
      if (document.getElementById('ipma-cis-cvmb-statistics-actions-portal')) {
        setPortalReady(true);
      }
    } else {
      if (document.getElementById(`${type}-cis-buttons-portal`)) {
        setPortalReady(true);
      }
    }
  }, [isIpmaCvmb, type]);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const filteredFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== ''),
      );
      await exportCertificateHoldersToExcel(filteredFilters);
    } catch (error) {
      console.error('Error exporting certificate holders:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloadingTemplate(true);
      await downloadCertificateHoldersImportTemplate();
    } catch (error) {
      console.error('Error downloading template:', error);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.title || ''}
                  onFilter={(val) => handleColumnFilterChange('title', val)}
                  filterOptions={titleOptions}
                  filterPlaceholder={t('page.user.cis.certificateHolders.titlePlaceholder')}
                  label={t('page.user.cis.certificateHolders.title')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.firstName || ''}
                  onFilter={(val) => handleColumnFilterChange('firstName', val)}
                  filterPlaceholder={t('page.user.cis.certificateHolders.firstNamePlaceholder')}
                  label={t('page.user.cis.certificateHolders.firstName')}
                  sort
                  sortDirection={getSortDirection('firstName')}
                  onSort={(dir) => handleColumnSort('firstName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.middleName || ''}
                  onFilter={(val) => handleColumnFilterChange('middleName', val)}
                  filterPlaceholder={t('page.user.cis.certificateHolders.middleNamePlaceholder')}
                  label={t('page.user.cis.certificateHolders.middleName')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.lastName || ''}
                  onFilter={(val) => handleColumnFilterChange('lastName', val)}
                  filterPlaceholder={t('page.user.cis.certificateHolders.lastNamePlaceholder')}
                  label={t('page.user.cis.certificateHolders.lastName')}
                  sort
                  sortDirection={getSortDirection('lastName')}
                  onSort={(dir) => handleColumnSort('lastName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.email || ''}
                  onFilter={(val) => handleColumnFilterChange('email', val)}
                  filterPlaceholder={t('page.user.cis.certificateHolders.emailPlaceholder')}
                  label={t('page.user.cis.certificateHolders.email')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range-year-select"
                  filterValueFrom={filters.birthDateFrom || ''}
                  filterValueTo={filters.birthDateTo || ''}
                  filterValueYear={filters.birthDateYear || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('birthDateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('birthDateTo', val)}
                  onFilterYear={(val) => handleColumnFilterChange('birthDateYear', val)}
                  filterPlaceholder={t('page.user.cis.certificateHolders.birthDatePlaceholder')}
                  label={t('page.user.cis.certificateHolders.birthDate')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.status || ''}
                  onFilter={(val) => handleColumnFilterChange('status', val)}
                  filterOptions={statusOptions}
                  filterPlaceholder={t('page.user.cis.certificateHolders.statusPlaceholder')}
                  label={t('page.user.cis.certificateHolders.status')}
                />
              </CisTable.StickyHeading>
              {!isIpmaCvmb && (
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('page.user.cis.certificateHolders.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              )}
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={isIpmaCvmb ? 7 : 8}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={isIpmaCvmb ? 7 : 8}>
                  {t('global.noResultsFound')}
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificateHolders.title')}</Table.Label>
                      {item.title}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificateHolders.firstName')}</Table.Label>
                      {item.firstName}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificateHolders.middleName')}</Table.Label>
                      {item.middleName}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificateHolders.lastName')}</Table.Label>
                      {item.lastName}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificateHolders.email')}</Table.Label>
                      {item.email}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificateHolders.birthDate')}:</Table.Label>
                      {item.birthDate && moment(item.birthDate).format('DD/MM/YYYY')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificateHolders.status')}</Table.Label>
                      {statusOptions.find((x) => x.value === item.status)?.label}
                    </CisTable.Cell>
                    {!isIpmaCvmb && (
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Table.Label>{t('page.user.cis.certificateHolders.actions')}:</Table.Label>
                        <Row style={{ gap: '3px', justifyContent: 'center' }}>
                          {manageCisPermission && (
                            <ButtonIcon
                              icon="edit"
                              secondary
                              as={Button}
                              onClick={() => {
                                setProfile(item);
                                history.push(`/${type}/cis/certificate-holders/edit`);
                              }}
                            />
                          )}
                          <ButtonIcon
                            icon="file"
                            secondary
                            as={Button}
                            onClick={() => {
                              setCertificatesFilters({
                                name: 'certificateHolder',
                                value: `${item.firstName} ${item.lastName}`,
                              });
                              history.push(`/${type}/cis/certificates`);
                            }}
                          />
                          {manageCisPermission && (
                            <ButtonIcon
                              icon="trash"
                              onClick={() => {
                                setOpenConfirmModal(true);
                                setSelectedItem(item);
                              }}
                            />
                          )}
                        </Row>
                      </CisTable.StickyActionCell>
                    )}
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.certificateHolders.confirmDelete')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeCertificateHolder(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        !isIpmaCvmb &&
        ReactDOM.createPortal(
          <Row style={{ alignItems: 'center', justifyContent: 'end' }}>
            {manageCisPermission && (
              <>
                <Col size={{ xs: 2 / 5 }}>
                  <Button secondary as={Link} to={`/${type}/cis/certificate-holders/create`}>
                    {t('page.user.cis.certificateHolders.new')}
                  </Button>
                </Col>
                <Col size={{ xs: 1 / 4 }}>
                  <Button secondary disabled={isDownloadingTemplate} onClick={handleDownload}>
                    {isDownloadingTemplate ? t('global.downloading') : t('global.downloadTemplate')}
                  </Button>
                </Col>
                <Col size={{ xs: 3 / 20 }}>
                  <Button secondary as={Link} to={`/${type}/cis/certificate-holders/import`}>
                    {t('page.user.cis.certificateHolders.import')}
                  </Button>
                </Col>
              </>
            )}
            <Col size={{ xs: 1 / 5 }}>
              <Button secondary onClick={handleExport} disabled={isExporting}>
                {isExporting ? t('global.exporting') : t('global.export')}
              </Button>
            </Col>
          </Row>,
          document.getElementById(`${type}-cis-buttons-portal`),
        )}
      {portalReady &&
        isIpmaCvmb &&
        ReactDOM.createPortal(
          <Button secondary onClick={handleExport} disabled={isExporting}>
            {isExporting ? t('global.exporting') : t('global.export')}
          </Button>,
          document.getElementById('ipma-cis-cvmb-statistics-actions-portal'),
        )}
    </PageContent>
  );
};

CertificateHolders.propTypes = {
  loading: PropTypes.bool,
  type: PropTypes.oneOf(['ipma', 'ma', 'ipma-cvmb']),
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeCertificateHolder: PropTypes.func.isRequired,
  setCertificatesFilters: PropTypes.func.isRequired,
};

CertificateHolders.defaultProps = {
  loading: false,
};

export default CertificateHolders;
